import {
  Button,
  OutlinedSelect,
  OutlinedSelectOptionType,
  Table,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import Loading from '../../../../../components/info/Loading';
import SectionTitle from '../../../../../components/text/SectionTitle';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';

import {
  AccrualBase,
  LateInterestOverview,
  LateInterestRateItem,
  deleteLateInterestRate,
  getLateInterest,
  updateLateInterestBase,
} from './api';
import { ConfigModal } from './components/ConfigModal';
import { useRateColumns } from './hooks/useRateColumns';

const dictPrefix = 'financialPortal.settings.lateInterest';

const BASE_VALUES: AccrualBase[] = ['totalResidual', 'capitalOnly'];

export default function LateInterestSection() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<LateInterestOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<LateInterestRateItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getLateInterest();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setOverview(data);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  // Refresca sin volver a poner `isLoading`: un refresco parcial no debe hacer
  // parpadear la sección entera.
  const handleSaved = useCallback(() => {
    setIsModalOpen(false);
    setEditing(null);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleNew = useCallback(() => {
    setEditing(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((item: LateInterestRateItem) => {
    setEditing(item);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    (item: LateInterestRateItem) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${dictPrefix}.confirmDelete.title`),
        subtitle: t(`${dictPrefix}.confirmDelete.subtitle`),
        btnConfirm: {
          text: t(`${dictPrefix}.confirmDelete.confirm`),
          onConfirm: async () => {
            const { error } = await deleteLateInterestRate(item.id);
            if (error) {
              addToast({
                icon: 'error',
                color: 'danger',
                text: t(`${dictPrefix}.deleteError`),
              });
              return;
            }
            addToast({
              icon: 'success',
              color: 'success',
              text: t(`${dictPrefix}.deleteSuccess`),
            });
            setRefreshToken((prev) => prev + 1);
          },
        },
        btnClose: { text: t(`${dictPrefix}.confirmDelete.cancel`) },
      });
    },
    [t],
  );

  const columns = useRateColumns({ onEdit: handleEdit, onDelete: handleDelete });

  const baseOptions = useMemo<OutlinedSelectOptionType[]>(
    () =>
      BASE_VALUES.map((value) => ({
        value,
        label: t(`${dictPrefix}.baseOptions.${value}`),
      })),
    [t],
  );

  const handleBaseChange = useCallback(
    async (option: OutlinedSelectOptionType | null) => {
      if (!option) {
        return;
      }
      const base = option.value as AccrualBase;
      const { error } = await updateLateInterestBase(base);
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.baseError`),
        });
        return;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.baseSaved`),
      });
      setRefreshToken((prev) => prev + 1);
    },
    [t],
  );

  if (isLoading) {
    return <Loading insideCard />;
  }
  if (!overview) {
    return null;
  }

  return (
    <>
      <Row className="align-items-start">
        <Col>
          <SectionTitle text={t(`${dictPrefix}.title`)} />
          <p>{t(`${dictPrefix}.subtitle`)}</p>
        </Col>
        <Col xs="auto" style={{ minWidth: 280 }}>
          <OutlinedSelect
            name="base"
            label={t(`${dictPrefix}.baseLabel`)}
            options={baseOptions}
            value={baseOptions.find((option) => option.value === overview.base)}
            onChange={handleBaseChange}
            isClearable={false}
          />
        </Col>
        <Col xs="auto">
          <Button
            text={t(`${dictPrefix}.newConfig`)}
            onClick={handleNew}
            size="sm"
            className="mb-1"
            outlined
          />
        </Col>
      </Row>
      <Table
        columns={columns}
        data={overview.items}
        noResultsText={
          <>
            <TableEmptyContent
              title={t(`${dictPrefix}.empty.title`)}
              subtitle={t(`${dictPrefix}.empty.subtitle`)}
            />
            <Button
              text={t(`${dictPrefix}.empty.action`)}
              onClick={handleNew}
              outlined
              size="sm"
              className="mx-auto mb-4"
            />
          </>
        }
      />
      <ConfigModal
        isOpen={isModalOpen}
        rateBasis={overview.rateBasis}
        item={editing}
        onClose={() => setIsModalOpen(false)}
        onSaved={handleSaved}
      />
    </>
  );
}
